import { useState } from 'react'
import { Outlet } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { TwoColumnLayout } from '@factorialco/factorial-one-react'
import { OneEmptyState } from '@factorialco/factorial-one-react/dist/experimental'

import { FeatureWall } from 'modules/core/components/FeatureWall'
import Loading from 'modules/core/components/Loading'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import FileUploaderModal from 'modules/locations/components/WorkAreas/FileUploaderModal'
import LocationWorkSpaces from 'modules/locations/components/WorkSpaces'
import { AlertMessage } from 'modules/locations/components/WorkSpaces/AlertMessage'
import { PinAlert } from 'modules/locations/components/WorkSpaces/PinAlert'
import { WorkSpaceContext } from 'modules/locations/contexts/workSpaces/WorkSpacesContext'
import { useWorkAreaMapAvailability } from 'modules/locations/hooks/useWorkAreaMapAvailability'
import { WorkSpacesMap } from 'modules/space_control/components/WorkSpacesMap'
import { MAP_MODE } from 'modules/space_control/components/WorkSpacesMap/types'
import { WorkSpace } from 'modules/space_control/hooks/useWorkSpace'
import { useWorkSpacesWithBookings } from 'modules/space_control/hooks/useWorkSpacesWithBookings'
import {
  SETTINGS_EVENT,
  SettingsEventType,
  useSettingsMapEvents,
} from 'modules/space_control/hooks/workSpacesMap/useSettingsMapEvents'

const WorkSpacesPage = () => {
  const { id, workAreaId } = useStrictParams<'id' | 'workAreaId'>()
  const [selectedWorkSpace, setSelectedWorkSpace] = useState<WorkSpace | null>(null)
  const [hoveredWorkSpaceId, setHoveredWorkSpaceId] = useState<number | null>(null)
  const [showUploader, setShowUploader] = useState(false)

  const handleSettingsEvent = (event: SettingsEventType, payload?: Record<string, unknown>) => {
    switch (event) {
      case SETTINGS_EVENT.PIN_PLACED:
        setSelectedWorkSpace(null)
        break
      case SETTINGS_EVENT.PIN_DELETED:
        setSelectedWorkSpace(null)
        refetchWorkSpaces()
        break
      case SETTINGS_EVENT.PIN_HOVERED:
        setHoveredWorkSpaceId(typeof payload?.workSpaceId === 'number' ? payload.workSpaceId : null)
        break
    }
  }

  const { loading: mapLoading, workAreaMap } = useWorkAreaMapAvailability({
    workAreaId: +workAreaId,
  })

  const {
    workSpaces,
    loading: workSpacesLoading,
    refetch: refetchWorkSpaces,
  } = useWorkSpacesWithBookings({
    locationIds: [+id],
    workAreaIds: [+workAreaId],
    date: new Date().toISOString().split('T')[0] || new Date().toISOString().slice(0, 10),
  })

  const { actions, alertWorkSpace, handleDelete, isPlacingPin, isDeletingPin, dismissAlert } =
    useSettingsMapEvents({
      selectedWorkSpace,
      mapId: workAreaMap?.id,
      workSpaces,
      onEvent: handleSettingsEvent,
    })

  const sideContent = (
    <LocationWorkSpaces
      workAreaId={+workAreaId}
      locationId={+id}
      selectedWorkSpace={selectedWorkSpace}
      hoveredWorkSpaceId={hoveredWorkSpaceId}
      onWorkSpaceSelect={(ws) => {
        dismissAlert()
        setSelectedWorkSpace(ws)
      }}
    />
  )

  const mainContent = (
    <FeatureWall featureName="dev_space_control_map_work_areas">
      {workAreaMap?.image?.url ? (
        <Box position="relative">
          <Box
            position="absolute"
            style={{
              top: '10%',
              left: '20%',
              right: '20%',
              zIndex: '9999',
              transform: 'translateY(-100%)',
              backgroundColor: 'white',
              borderRadius: '10px',
            }}
          >
            {alertWorkSpace && !isDeletingPin && (
              <PinAlert workSpace={alertWorkSpace} onDelete={handleDelete} />
            )}

            <AlertMessage
              message={
                isPlacingPin
                  ? i18n.t('space_control.map.placing_pin')
                  : isDeletingPin
                  ? i18n.t('space_control.map.deleting_pin')
                  : selectedWorkSpace
                  ? i18n.t('space_control.map.click_to_place_pin')
                  : undefined
              }
            />
          </Box>
          <WorkSpacesMap
            workAreaId={workAreaId}
            workSpaces={workSpaces}
            actions={actions}
            mapMode={selectedWorkSpace ? MAP_MODE.PLACE_PIN : MAP_MODE.DEFAULT}
          />
        </Box>
      ) : (
        <Box width="s400">
          <OneEmptyState
            title={i18n.t('space_control.overview.no_map_configured')}
            description=""
            emoji={'🗺️'}
            actions={[
              {
                label: i18n.t('settings.locations.location.work_areas.map.add_map'),
                onClick: () => setShowUploader(true),
                type: 'default',
                variant: 'outline',
              },
            ]}
          />
          {showUploader && (
            <FileUploaderModal
              workAreaId={+workAreaId}
              onClose={() => setShowUploader(false)}
              onSuccess={() => {
                refetchWorkSpaces()
                setShowUploader(false)
              }}
            />
          )}
        </Box>
      )}
    </FeatureWall>
  )

  if (mapLoading || workSpacesLoading) {
    return (
      <ResponsivePage.Body variant="full">
        <Loading />
      </ResponsivePage.Body>
    )
  }

  return (
    <>
      <ResponsivePage.Body variant="full">
        <WorkSpaceContext.Provider
          value={{
            onDeleted: refetchWorkSpaces,
          }}
        >
          <TwoColumnLayout sideContent={sideContent}>{mainContent}</TwoColumnLayout>
        </WorkSpaceContext.Provider>
      </ResponsivePage.Body>
      <Outlet />
    </>
  )
}

export default WorkSpacesPage
